import { createBrowserClient, createServerClient } from '@supabase/ssr'
import {
    parse,
    serialize,
    parseSetCookie,
    splitSetCookieString,
    type SetCookie,
} from 'cookie-es'
import type { User } from '@supabase/supabase-js'

export default function useSupabase() {
    const nuxtApp = useNuxtApp()
    const event = useRequestEvent(nuxtApp)
    const runtimeConfig = useRuntimeConfig(event)

    nuxtApp.runWithContext

    if (import.meta.server && event) {
        return createServerClient(
            runtimeConfig.public.supabaseURL,
            runtimeConfig.public.supabaseKey,
            {
                cookies: {
                    getAll() {
                        const cookies = parse(
                            event.node.req.headers.cookie || ''
                        )
                        return Object.keys(cookies).map(cookie => ({
                            name: cookie,
                            value: cookies[cookie]!,
                        }))
                    },
                    setAll(cookies) {
                        cookies.forEach(cookie => {
                            const newCookie = serialize(
                                cookie.name,
                                cookie.value,
                                { path: '/', ...cookie.options }
                            )

                            const getSetCookie = () => {
                                const setCookie =
                                    event.node.req.headers['set-cookie']
                                if (!setCookie || setCookie.length === 0) {
                                    return []
                                }
                                return splitSetCookieString(setCookie)
                            }

                            const currentCookies = getSetCookie()

                            if (currentCookies.length === 0) {
                                event.node.res.setHeader(
                                    'set-cookie',
                                    newCookie
                                )
                                return
                            }

                            function _getDistinctCookieKey(
                                name: string,
                                options: Partial<SetCookie>
                            ) {
                                return [
                                    name,
                                    options.domain || '',
                                    options.path || '/',
                                    Boolean(options.secure),
                                    Boolean(options.httpOnly),
                                    Boolean(options.sameSite),
                                ].join(';')
                            }

                            const newCookieKey = _getDistinctCookieKey(
                                cookie.name,
                                (cookie.options || {}) as SetCookie
                            )

                            event.node.res.removeHeader('set-cookie')

                            for (const c of currentCookies) {
                                const _key = _getDistinctCookieKey(
                                    c.split('=')?.[0] || '',
                                    parseSetCookie(c)
                                )
                                if (_key === newCookieKey) {
                                    continue
                                }
                                event.node.res.appendHeader('set-cookie', c)
                            }

                            event.node.res.appendHeader('set-cookie', newCookie)
                        })
                    },
                },
            }
        )
    }

    return createBrowserClient(
        runtimeConfig.public.supabaseURL,
        runtimeConfig.public.supabaseKey
    )
}

export const useUser = () => useState<User | null>('user', () => null)
export const useProfile = () => useState<Database['public']['Tables']['profiles']['Row'] | null>('profile', () => null)
